// Pure layout math for the roadmap timeline (kanban d71a3beb, owner-approved
// design round 4 on review bdebb0e3). No DOM, no React: everything here is
// unit-testable and the chart component only draws what these functions say.
//
// The honesty rule that shaped the design (2026-09-27 assessment): a bar is
// drawn only between STATED dates. An initiative nobody has dated gets a
// placeholder strip from its first mention to today, dashed, so the absence
// reads as a fact and not as a plan. No illustrative dates in production.

import { scaleTime, type ScaleTime } from 'd3-scale';
import { timeDay, timeMonth, timeWeek, type TimeInterval } from 'd3-time';
import { timeFormat } from 'd3-time-format';
import type {
  InitiativeAction,
  InitiativeState,
  RoadmapEvent,
  ScheduleHealth,
} from '../../services/roadmapReports';
import { liveActions, NO_TEAM, NO_TEAM_COLOR, type TeamColors, accentTeamOf } from './roadmapViewUtils';

export type HorizonPreset = '2w' | '30d' | 'quarter' | 'fit';
export const HORIZON_LABELS: Record<HorizonPreset, string> = {
  '2w': '2 weeks',
  '30d': '30 days',
  quarter: 'Quarter',
  fit: 'Fit',
};
export const DEFAULT_HORIZON: HorizonPreset = '30d';

/** Delta Force cost-down projects live under the program initiative; the
 * timeline can give them their own lane so the Delta team lane stays readable. */
export const DELTA_PROGRAM_KEY = 'delta-cost-down-program';
export const DELTA_PROJECTS_LANE = 'Delta projects';
export type DeltaLaneMode = 'own' | 'team';

export const ROW_HEIGHT = 32;
export const HEADER_HEIGHT = 44;
export const CHART_PAD = 12;

export const HEALTH_COLORS: Record<ScheduleHealth, string> = {
  on_track: '#15803d',
  late: '#b91c1c',
  at_risk: '#c2410c',
  stuck: '#b45309',
  no_target: '#64748b',
  done: '#16a34a',
  parked: '#6366f1',
  dropped: '#94a3b8',
};

const DAY_MS = 86_400_000;

export function parseDay(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const fmtDay = timeFormat('%b %-d');
export const fmtDayYear = timeFormat('%b %-d, %Y');

// ── Spans ────────────────────────────────────────────────────────────────────

export interface InitiativeSpan {
  key: string;
  /** Where the bar starts. */
  start: Date;
  /** Where the bar ends. */
  end: Date;
  /** True when at least one of start / end was stated by the group. */
  dated: boolean;
  startStated: boolean;
  endStated: boolean;
  /** When nothing was stated: first mention to today, drawn as a placeholder. */
  placeholder: boolean;
  target: Date | null;
}

export function initiativeSpan(i: InitiativeState, today: Date): InitiativeSpan {
  const start = parseDay(i.started_on);
  const target = parseDay(i.target_date);
  const firstSeen = parseDay(i.first_seen_at) ?? today;
  if (!start && !target) {
    const s = firstSeen > today ? today : firstSeen;
    return {
      key: i.initiative_key,
      start: s,
      end: today > s ? today : addDays(s, 1),
      dated: false,
      startStated: false,
      endStated: false,
      placeholder: true,
      target: null,
    };
  }
  const s = start ?? (firstSeen < (target as Date) ? firstSeen : addDays(target as Date, -1));
  let e = target ?? (today > s ? today : addDays(s, 1));
  if (e <= s) e = addDays(s, 1);
  return {
    key: i.initiative_key,
    start: s,
    end: e,
    dated: true,
    startStated: Boolean(start),
    endStated: Boolean(target),
    placeholder: false,
    target,
  };
}

export interface ActionSpan {
  key: string;
  start: Date | null;
  end: Date | null;
  dated: boolean;
}

/** A sub-action is drawn only on its stated dates: a bar between start and
 * end, a pin when only one is known, nothing (a "no dates stated" note) otherwise. */
export function actionSpan(a: InitiativeAction): ActionSpan {
  const start = parseDay(a.started_on);
  const end = parseDay(a.status === 'done' && a.completed_on ? a.completed_on : a.target_date);
  return { key: a.key, start, end, dated: Boolean(start || end) };
}

// ── Lanes and rows ───────────────────────────────────────────────────────────

const LANE_ORDER = ['AI', 'Alpha team', 'Delta team', DELTA_PROJECTS_LANE];

export function isDeltaProject(i: InitiativeState): boolean {
  return i.initiative_key.startsWith('delta-') && i.initiative_key !== DELTA_PROGRAM_KEY;
}

/** The lane an initiative is drawn in: its OWNING team (v9); while nobody is
 * named, its first non-AI team (the task force does the work; AI assists),
 * else its first team. Delta projects get their own lane when asked. */
export function laneOf(i: InitiativeState, deltaLane: DeltaLaneMode, preferTeams: readonly string[] = []): string {
  if (deltaLane === 'own' && isDeltaProject(i)) return DELTA_PROJECTS_LANE;
  const teams = i.teams ?? [];
  if (teams.length === 0) return NO_TEAM;
  // Under a team filter a shared initiative sits in the lane of a team that
  // was asked for, not in another team's lane (kanban 0baf9993).
  if (preferTeams.length > 0) {
    const wanted = new Set(preferTeams.map((t) => t.toLowerCase()));
    const hit = teams.find((t) => wanted.has(t.toLowerCase()));
    if (hit) return hit;
  }
  return accentTeamOf(i) ?? teams[0];
}

export function laneColor(lane: string, colors: TeamColors): string {
  if (lane === DELTA_PROJECTS_LANE) return colors['Delta team'] ?? NO_TEAM_COLOR;
  return colors[lane] ?? NO_TEAM_COLOR;
}

export type TimelineRow =
  | { kind: 'lane'; lane: string; color: string; count: number }
  | {
      kind: 'init'; lane: string; color: string; initiative: InitiativeState; span: InitiativeSpan;
      /** The sub-actions an expanded row lists (done ones only when shown or retained). */
      actions: InitiativeAction[];
      expanded: boolean;
      /** Done sub-actions left out of `actions`. */
      hiddenDone: number;
      /** A done initiative kept only because shown work still lists it as a blocker. */
      retained: boolean;
    }
  | {
      kind: 'act'; lane: string; color: string; initiative: InitiativeState; action: InitiativeAction; span: ActionSpan; last: boolean;
      /** A done sub-action kept only because an unfinished sibling still waits on it. */
      retained: boolean;
    };

export interface BuildRowsOptions {
  expanded: ReadonlySet<string>;
  deltaLane: DeltaLaneMode;
  today: Date;
  teamColors: TeamColors;
  /** Teams the page is filtered to; see laneOf. */
  preferTeams?: readonly string[];
  /** Draw done sub-actions too (the "show done / dropped" checkbox). Off by default. */
  includeDone?: boolean;
  /** Initiative keys from timelineSet().retained. */
  retained?: ReadonlySet<string>;
}

export interface ShownActions {
  shown: InitiativeAction[];
  /** Keys of done actions kept because an unfinished sibling lists them in depends_on. */
  retained: ReadonlySet<string>;
  hiddenDone: number;
}

/** The sub-actions an expanded timeline row lists. Done ones are hidden unless
 * asked for, except a done action an unfinished sibling still waits on: it
 * stays so the arrow between the two survives. */
export function shownActions(actions: InitiativeAction[], includeDone: boolean): ShownActions {
  const live = liveActions(actions);
  if (includeDone) return { shown: live, retained: new Set(), hiddenDone: 0 };
  const waitedOn = new Set(live.filter((a) => a.status !== 'done').flatMap((a) => a.depends_on));
  const retained = new Set(live.filter((a) => a.status === 'done' && waitedOn.has(a.key)).map((a) => a.key));
  const shown = live.filter((a) => a.status !== 'done' || retained.has(a.key));
  return { shown, retained, hiddenDone: live.length - shown.length };
}

/** Sort inside a lane: dated first (soonest target first), then placeholders by title. */
function compareInLane(a: { initiative: InitiativeState; span: InitiativeSpan }, b: { initiative: InitiativeState; span: InitiativeSpan }): number {
  if (a.span.dated !== b.span.dated) return a.span.dated ? -1 : 1;
  if (a.span.target && b.span.target && a.span.target.getTime() !== b.span.target.getTime()) {
    return a.span.target.getTime() - b.span.target.getTime();
  }
  if (Boolean(a.span.target) !== Boolean(b.span.target)) return a.span.target ? -1 : 1;
  // numeric: Δ2 sorts before Δ10
  return a.initiative.title.localeCompare(b.initiative.title, undefined, { numeric: true });
}

export function buildRows(initiatives: InitiativeState[], opts: BuildRowsOptions): TimelineRow[] {
  const groups = new Map<string, { initiative: InitiativeState; span: InitiativeSpan }[]>();
  for (const i of initiatives) {
    if (i.status === 'dropped') continue;
    const lane = laneOf(i, opts.deltaLane, opts.preferTeams);
    const list = groups.get(lane) ?? [];
    list.push({ initiative: i, span: initiativeSpan(i, opts.today) });
    groups.set(lane, list);
  }
  const lanes = [...groups.keys()].sort((a, b) => {
    const ia = LANE_ORDER.indexOf(a);
    const ib = LANE_ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    if (a === NO_TEAM) return 1;
    if (b === NO_TEAM) return -1;
    return a.localeCompare(b);
  });
  const rows: TimelineRow[] = [];
  for (const lane of lanes) {
    const color = laneColor(lane, opts.teamColors);
    const items = (groups.get(lane) ?? []).sort(compareInLane);
    rows.push({ kind: 'lane', lane, color, count: items.length });
    for (const { initiative, span } of items) {
      const { shown: actions, retained, hiddenDone } = shownActions(initiative.actions, opts.includeDone ?? false);
      const expanded = opts.expanded.has(initiative.initiative_key) && actions.length > 0;
      rows.push({
        kind: 'init', lane, color, initiative, span, actions, expanded, hiddenDone,
        retained: opts.retained?.has(initiative.initiative_key) ?? false,
      });
      if (expanded) {
        actions.forEach((action, idx) => {
          rows.push({
            kind: 'act', lane, color, initiative, action, span: actionSpan(action),
            last: idx === actions.length - 1, retained: retained.has(action.key),
          });
        });
      }
    }
  }
  return rows;
}

export function expandableKeys(initiatives: InitiativeState[], includeDone = false): string[] {
  return initiatives
    .filter((i) => i.status !== 'dropped' && shownActions(i.actions, includeDone).shown.length > 0)
    .map((i) => i.initiative_key);
}

export function rowY(index: number): number {
  return HEADER_HEIGHT + index * ROW_HEIGHT;
}

export function chartHeight(rowCount: number): number {
  return HEADER_HEIGHT + rowCount * ROW_HEIGHT;
}

// ── Window ───────────────────────────────────────────────────────────────────

export function horizonWindow(preset: HorizonPreset, today: Date, spans: InitiativeSpan[]): [Date, Date] {
  switch (preset) {
    case '2w':
      return [addDays(today, -4), addDays(today, 10)];
    case '30d':
      return [addDays(today, -9), addDays(today, 21)];
    case 'quarter':
      return [addDays(today, -30), addDays(today, 61)];
    case 'fit': {
      let lo = today;
      let hi = today;
      for (const s of spans) {
        if (s.start < lo) lo = s.start;
        if (s.end > hi) hi = s.end;
      }
      if (hi.getTime() - lo.getTime() < 14 * DAY_MS) hi = addDays(lo, 14);
      return [addDays(lo, -3), addDays(hi, 3)];
    }
    default:
      return [addDays(today, -9), addDays(today, 21)];
  }
}

export function makeScale(win: [Date, Date], width: number): ScaleTime<number, number> {
  return scaleTime().domain(win).range([CHART_PAD, Math.max(width - CHART_PAD, CHART_PAD + 1)]);
}

export interface TickSpec {
  /** Grid line interval. */
  grid: TimeInterval;
  /** Lower-row labels (null = only the month row). */
  lower: TimeInterval | null;
  lowerFormat: (d: Date) => string;
}

export function tickSpec(win: [Date, Date]): TickSpec {
  const days = (win[1].getTime() - win[0].getTime()) / DAY_MS;
  if (days <= 21) return { grid: timeDay, lower: timeDay, lowerFormat: timeFormat('%a %-d') };
  if (days <= 130) return { grid: timeWeek, lower: timeWeek, lowerFormat: timeFormat('%-d %b') };
  return { grid: timeMonth, lower: null, lowerFormat: timeFormat('%b') };
}

export const monthFormat = timeFormat('%B');
export { timeMonth };

// ── Placement ────────────────────────────────────────────────────────────────

export interface Anchor {
  x1: number;
  x2: number;
  cy: number;
  placeholder: boolean;
}

/** Clamp a span into the window; returns null when it lies entirely outside. */
export function clampSpan(
  x: ScaleTime<number, number>,
  start: Date,
  end: Date,
  win: [Date, Date],
): { x1: number; x2: number } | null {
  if (end < win[0] || start > win[1]) return null;
  const s = start < win[0] ? win[0] : start;
  const e = end > win[1] ? win[1] : end;
  const x1 = x(s);
  const x2 = Math.max(x(e), x1 + 4);
  return { x1, x2 };
}

export function inWindow(d: Date | null, win: [Date, Date]): d is Date {
  return Boolean(d && d >= win[0] && d <= win[1]);
}

/** Dates on which a run recorded a change to this initiative (movement ticks). */
export function movementDates(events: RoadmapEvent[], key: string): { date: Date; fields: string[] }[] {
  const byDay = new Map<string, Set<string>>();
  for (const e of events) {
    if (e.initiative_key !== key) continue;
    const day = (e.report_date ?? e.created_at).slice(0, 10);
    const set = byDay.get(day) ?? new Set<string>();
    for (const f of Object.keys(e.changes ?? {})) set.add(f);
    if (e.kind === 'created') set.add('first tracked');
    byDay.set(day, set);
  }
  return [...byDay.entries()]
    .map(([day, fields]) => ({ date: parseDay(day) as Date, fields: [...fields].sort() }))
    .filter((m) => m.date)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

/** Cubic path from the blocker's end to the dependent's start.
 *
 * A dependent with no stated dates is a placeholder strip that runs from its
 * first mention to today, so its left edge is usually far to the LEFT of the
 * blocker's end and a literal end-to-start arrow would loop backwards across
 * the chart. In that case the arrow lands on the placeholder just after the
 * blocker's end instead: "this is where the dependent could start". */
export function arrowPath(from: Anchor, to: Anchor): string {
  // Start a little past the bar end so the arrow never sits on the target diamond.
  const x1 = from.x2 + 8;
  const y1 = from.cy;
  let x2 = to.x1;
  if (to.placeholder && to.x1 < from.x2) x2 = Math.min(to.x2, from.x2 + 36);
  const y2 = to.cy;
  const dx = Math.max(24, Math.abs(x2 - x1) / 2);
  return `M${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
}

export interface DependencyEdge {
  fromKey: string;
  toKey: string;
  /** Initiative keys the arrow highlights (both ends). */
  fromRow: string;
  toRow: string;
  dashed: boolean;
  label: string;
  /** Initiative to open on click (the dependent), and the modal section. */
  openKey: string;
}

export function actionAnchorKey(initiativeKey: string, actionKey: string): string {
  return `${initiativeKey}/${actionKey}`;
}

/** Every dependency edge drawable from the rows on screen. */
export function dependencyEdges(rows: TimelineRow[], anchors: Map<string, Anchor>, byKey: Map<string, InitiativeState>): DependencyEdge[] {
  const edges: DependencyEdge[] = [];
  for (const r of rows) {
    if (r.kind === 'init') {
      for (const dep of r.initiative.depends_on) {
        const src = byKey.get(dep);
        const a = anchors.get(dep);
        const b = anchors.get(r.initiative.initiative_key);
        if (!src || !a || !b) continue;
        edges.push({
          fromKey: dep,
          toKey: r.initiative.initiative_key,
          fromRow: dep,
          toRow: r.initiative.initiative_key,
          dashed: a.placeholder || b.placeholder,
          label: `${src.title} must finish before ${r.initiative.title} can proceed`,
          openKey: r.initiative.initiative_key,
        });
      }
    } else if (r.kind === 'act') {
      for (const dep of r.action.depends_on) {
        const fromKey = actionAnchorKey(r.initiative.initiative_key, dep);
        const toKey = actionAnchorKey(r.initiative.initiative_key, r.action.key);
        const a = anchors.get(fromKey);
        const b = anchors.get(toKey);
        const sib = liveActions(r.initiative.actions).find((z) => z.key === dep);
        if (!sib || !a || !b) continue;
        edges.push({
          fromKey,
          toKey,
          fromRow: r.initiative.initiative_key,
          toRow: r.initiative.initiative_key,
          dashed: a.placeholder || b.placeholder,
          label: `${sib.title} before ${r.action.title}`,
          openKey: r.initiative.initiative_key,
        });
      }
    }
  }
  return edges;
}

/** Colour of a bar's outline by schedule health. */
export function healthStroke(i: InitiativeState): string {
  return HEALTH_COLORS[i.health] ?? NO_TEAM_COLOR;
}

/** A short description of the row's dates in words, for tooltips and the modal. */
export function datesInWords(span: InitiativeSpan, i: InitiativeState): string {
  if (span.placeholder) return `No stated dates. Tracked since ${fmtDay(span.start)}.`;
  const startWord = span.startStated ? `started ${fmtDay(span.start)}` : `tracked since ${fmtDay(span.start)}`;
  const endWord = span.endStated ? `target ${fmtDay(span.end)}` : 'no stated target';
  const blocker = i.external_blocker ? ` Waiting on ${i.external_blocker}.` : '';
  return `${startWord}, ${endWord}.${blocker}`;
}

/** The label column's sub-action pill: "1/7" on the row (the column is too
 * narrow for a sentence next to the title), the sentence on hover. It counts
 * every live sub-action, hidden done ones included, so the count stays honest. */
export function subActionChip(actions: InitiativeAction[]): { short: string; long: string } | null {
  const live = liveActions(actions);
  if (live.length === 0) return null;
  const done = live.filter((a) => a.status === 'done').length;
  return {
    short: `${done}/${live.length}`,
    long: `${live.length} sub-action${live.length === 1 ? '' : 's'}${done ? `, ${done} done` : ''}`,
  };
}

// ── Evidence grouping (the pop-up's Evidence section) ─────────────────────────

export type EvidenceKind = 'meeting' | 'knowledge bank' | 'email' | 'document' | 'other';

/** Group the agent's free-text citations by what they point at. */
export function evidenceKind(source: string): EvidenceKind {
  const s = source.trim();
  if (/^(?:recall\s+)?(?:meeting|delta_force_notes|fireflies|plaud|recall)\s+[0-9a-f]{6,}/i.test(s)) return 'meeting';
  if (/\bKB\b/i.test(s)) return 'knowledge bank';
  if (/^emails?\b/i.test(s)) return 'email';
  if (/\bdocument\b/i.test(s)) return 'document';
  return 'other';
}

export function groupEvidence(sources: string[]): [EvidenceKind, string[]][] {
  const order: EvidenceKind[] = ['meeting', 'email', 'document', 'knowledge bank', 'other'];
  const map = new Map<EvidenceKind, string[]>();
  for (const s of sources) {
    const k = evidenceKind(s);
    map.set(k, [...(map.get(k) ?? []), s]);
  }
  return order.filter((k) => map.has(k)).map((k) => [k, map.get(k) as string[]]);
}


/** A ledger row as the pop-up needs it to name a piece of evidence. */
export interface EvidenceSource {
  source_id: string;
  label: string | null;
  started_at: string | null;
}

/**
 * A piece of evidence in words: "meeting 56377d83" reads as the meeting's
 * title and day when the source ledger knows it. Anything the ledger cannot
 * name is shown as Merlin cited it.
 */
export function evidenceLabel(source: string, ledger: readonly EvidenceSource[]): string {
  const m = /^(?:recall\s+)?(?:meeting|delta_force_notes|fireflies|plaud|recall)\s+([0-9a-f]{6,})/i.exec(source.trim());
  if (!m) return source;
  const hit = ledger.find((l) => l.label && l.source_id.toLowerCase().startsWith(m[1].toLowerCase()));
  if (!hit || !hit.label) return source;
  const day = parseDay(hit.started_at);
  return day ? `${hit.label} (${fmtDay(day)})` : hit.label;
}

/**
 * Split the timeline's initiatives into the ones it can draw and the ones it
 * cannot. An initiative with no stated start or target, and no sub-action
 * with a date either, has nothing to place on a time axis: drawing it as a
 * line from "first mentioned" to today read as a bar (43 of 45 rows), so it is
 * listed under the chart instead.
 */
export function splitUndated(initiatives: InitiativeState[], today: Date): { drawn: InitiativeState[]; undated: InitiativeState[] } {
  const drawn: InitiativeState[] = [];
  const undated: InitiativeState[] = [];
  for (const i of initiatives) {
    if (i.status === 'dropped') continue;
    const datedAction = liveActions(i.actions).some((a) => a.started_on || a.target_date || a.completed_on);
    if (initiativeSpan(i, today).placeholder && !datedAction) undated.push(i);
    else drawn.push(i);
  }
  undated.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
  return { drawn, undated };
}
